import { ScrollArea } from "components/ScrollArea/ScrollArea";
import Table from "components/Table";
import { Paper } from "helpers/api";
import useLabels from "hooks/useLabels";
import { Building2, Users, BookOpen } from "lucide-react";
import { CitationGroup } from "./CitationGroup";
import { TablePlaceholder } from "./TablePlaceholder";

enum ContributorType {
  AUTHORS = "authors",
  JOURNALS = "journals",
  INSTITUTIONS = "institutions",
}

/**
 * Represents a contributor (author or journal) with their associated citations
 */
type Contributor = {
  /** The name of the contributor (author name or journal title) */
  name: string;
  /** Comma-separated string of citation numbers referencing this contributor. Citation numbers are 1-indexed. */
  citations: string;
};

/**
 * Props for the TopContributors component
 */
type TopContributorsProps = {
  /** Array of top contributing authors with their citation references */
  authors?: Contributor[];
  /** Array of top contributing journals with their citation references */
  journals?: Contributor[];
  /** Array of top contributing institutions with their citation references */
  institutions?: Contributor[];
  /** Array of all papers available for citation lookup and linking */
  papers?: Paper[];
  /** Callback function triggered when a user clicks on a citation */
  onClickCitation?: (
    paper: Paper,
    paperIndex: number,
    referenceLineNumber: number,
    associatedSentence: string
  ) => void;
};

/**
 * A table component that displays the most frequently cited authors and journals
 * in a research analysis. Shows contributor names alongside clickable citation
 * groups that reference the specific papers by each contributor.
 *
 * Features:
 * - Displays both authors and journals in a unified table
 * - Shows citation numbers as clickable groups
 * - Integrates with citation tracking and paper navigation
 * - Responsive design with horizontal scrolling on small screens
 * - Gracefully handles missing data by hiding the component
 *
 */
function TopContributors({
  authors,
  journals,
  institutions,
  papers,
  onClickCitation,
}: TopContributorsProps) {
  const [topContributorsLabels] = useLabels("markdown.top-contributors");

  // Don't render anything if no data is provided
  const hasAuthors = authors && authors.length > 0;
  const hasJournals = journals && journals.length > 0;
  const hasInstitutions = institutions && institutions.length > 0;

  if (!hasAuthors && !hasJournals && !hasInstitutions) {
    return null;
  }

  /**
   * Renders table rows for a specific type of contributor (authors or journals)
   *
   * @param contributors - Array of contributors to render
   * @param type - The type of contributor (AUTHORS or JOURNALS)
   * @returns JSX elements for the table rows
   */
  const renderTableRows = (
    contributors: Contributor[],
    type: ContributorType
  ) => {
    const config = {
      [ContributorType.AUTHORS]: {
        Icon: Users,
        label: topContributorsLabels.authors,
        keyPrefix: "author",
      },
      [ContributorType.JOURNALS]: {
        Icon: BookOpen,
        label: topContributorsLabels.journals,
        keyPrefix: "journal",
      },
      [ContributorType.INSTITUTIONS]: {
        Icon: Building2,
        label: topContributorsLabels.institutions,
        keyPrefix: "institution",
      },
    };
    const { Icon, label, keyPrefix } = config[type];

    // Ensure contributors is not undefined and is a list of objects
    if (!contributors || !Array.isArray(contributors)) {
      return null;
    }

    return contributors.map((contributor, index) => {
      const citations = contributor.citations
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id))
        .map((paperNumber) => ({ paperIndex: paperNumber - 1 })); // Convert 1-based to 0-based

      return (
        <Table.TR key={`${keyPrefix}-${index}`}>
          {/* Type column - only show for first item */}
          {index === 0 && (
            <Table.TD
              className="text-sm align-top"
              rowSpan={contributors.length}
            >
              <div className="flex gap-2 items-center">
                <Icon size={18} className="text-fg-muted" />
                <span className="font-medium text-fg-default">{label}</span>
              </div>
            </Table.TD>
          )}
          <Table.TD>
            <div className="line-clamp-3">
              {type === ContributorType.JOURNALS ? (
                <em>{contributor.name}</em>
              ) : (
                contributor.name
              )}
            </div>
          </Table.TD>
          <Table.TD>
            <CitationGroup
              citations={citations}
              isTableCitation={true}
              stableKey={`${keyPrefix}-${index}`}
              papers={papers}
              referenceLineNumber={0}
              associatedSentence={`${label.slice(0, -1)}: ${contributor.name}`}
              onClickCitation={onClickCitation}
              showPapercount={true}
            />
          </Table.TD>
        </Table.TR>
      );
    });
  };

  return (
    <ScrollArea orientation="horizontal" className="mb-3">
      <Table className="mt-0">
        <Table.THead>
          <Table.TR>
            <Table.TH className="min-w-[200px]">
              {topContributorsLabels.type}
            </Table.TH>
            <Table.TH className="min-w-[200px]">
              {topContributorsLabels.name}
            </Table.TH>
            <Table.TH className="min-w-[200px]">
              {topContributorsLabels.papers}
            </Table.TH>
          </Table.TR>
        </Table.THead>
        <Table.TBody>
          {hasAuthors && renderTableRows(authors!, ContributorType.AUTHORS)}
          {hasJournals && renderTableRows(journals!, ContributorType.JOURNALS)}
          {hasInstitutions &&
            renderTableRows(institutions!, ContributorType.INSTITUTIONS)}
        </Table.TBody>
      </Table>
    </ScrollArea>
  );
}

/**
 * Placeholder component shown while top contributors data is loading
 */
export const TopContributorsPlaceholder = () => {
  const [topContributorsLabels] = useLabels("markdown.top-contributors");
  return (
    <TablePlaceholder
      rows={2}
      headers={[
        topContributorsLabels.type,
        topContributorsLabels.name,
        topContributorsLabels.papers,
      ]}
    />
  );
};

/**
 * Formats citation string for markdown export
 *
 * @param citations - Comma-separated citation numbers
 * @returns Formatted citation string with brackets
 */
const formatCitations = (citations: string) => {
  return citations
    .split(",")
    .map((id) => `[${id}]`)
    .join("");
};

/**
 * Converts top contributors data to a single markdown table for plain text export
 *
 * @param props - TopContributors props containing authors and journals data
 * @returns Single formatted markdown table with Type, Name, and Papers columns
 */
export const TopContributorsMarkdown = ({
  authors,
  journals,
  institutions,
}: TopContributorsProps) => {
  const [topContributorsLabels] = useLabels("markdown.top-contributors");

  const authorsData = authors || [];
  const journalsData = journals || [];
  const institutionsData = institutions || [];

  // If no data, return empty string
  if (
    authorsData.length === 0 &&
    journalsData.length === 0 &&
    institutionsData.length === 0
  ) {
    return "";
  }

  let markdown = "| Type | Name | Papers |\n";
  markdown += "|------|------|--------|\n";

  // Add authors
  authorsData.forEach((author) => {
    markdown += `| ${topContributorsLabels.authors.slice(0, -1)} | ${
      author.name
    } | ${formatCitations(author.citations)} |\n`;
  });

  // Add journals
  journalsData.forEach((journal) => {
    markdown += `| ${topContributorsLabels.journals.slice(0, -1)} | *${
      journal.name
    }* | ${formatCitations(journal.citations)} |\n`;
  });

  // Add institutions
  institutionsData.forEach((institution) => {
    markdown += `| ${topContributorsLabels.institutions.slice(0, -1)} | ${
      institution.name
    } | ${formatCitations(institution.citations)} |\n`;
  });

  return markdown;
};

export default TopContributors;
